import RenderTabs from "components/Tabs/Tabs";
import React, { useCallback, useContext, useEffect } from "react";
import { ProjectOverview, TaskManager, UserManagement } from "./tabComponents";
import { fetchProjectById } from "actions/projectActions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getProjectData } from "selectors/projectSelector";
import { Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import {
  isAssignee,
  isHomeOwner,
  isProjectLimitedView,
  isProjectManager,
} from "core/utils/roleUtils";
import AppRoleContext from "context/AppRoleContext";
import { CustomBreadCrumb } from "components/BreadCrumb";
import ProjectDetails from "./tabComponents/ProjectDetails";
import { FileManager } from "./tabComponents/FileManager";
import get from "lodash/get";
import ProjectSchedule from "./tabComponents/ProjectSchedule";
import Warranty from "./tabComponents/warranty/Warranty";
import ProjectSuppliers from "./tabComponents/ProjectSuppliers";
import PunchListPage from "./tabComponents/PunchList";
import Invoices from "pages/invoice/Invoice";
import { getCompanyConfig } from "selectors/appSelector";
import ViewPropertyDetails from "pages/property/PropertyDetails";
import { formatProjectLabel } from "core/utils/appUtils";
import ChangeOrders from "pages/change_order/ChangeOrders";

const tabs = [
  {
    key: "overview",
    label: "Dashboard",
    TabComponent: ProjectOverview,
    hide: ({ appRoleDetails, projectData }) => {
      const projectUserRole = projectData?.projUserRole;
      return (
        !isProjectManager(appRoleDetails, projectUserRole) ||
        isAssignee(projectData.accessType)
      );
    },
  },
  {
    key: "projectDetails",
    label: "Project Details",
    transformLabel: true,
    TabComponent: ProjectDetails,
  },
  {
    key: "locationDetails",
    label: "Site Information",
    hide: ({ companyConfig }) => !companyConfig?.projEqualProp,
    TabComponent: ViewPropertyDetails,
  },
  {
    key: "jobs",
    label: "Jobs",
    TabComponent: TaskManager,
    hide: ({ appRoleDetails }) => isHomeOwner(appRoleDetails),
  },
  {
    key: "schedules",
    label: "Schedule",
    TabComponent: ProjectSchedule,
  },
  {
    key: "suppliers",
    label: "Subcontractors",
    // TabComponent: Analysis,
    TabComponent: ProjectSuppliers,
    hide: ({ appRoleDetails, projectData }) => {
      const projectUserRole = projectData?.projUserRole;
      return (
        !isProjectManager(appRoleDetails, projectUserRole) ||
        isAssignee(projectData.accessType) ||
        isHomeOwner(appRoleDetails)
      );
    },
  },
  {
    key: "files",
    label: "Documents",
    TabComponent: FileManager,
  },
  {
    key: "invoices",
    label: "Billing",
    TabComponent: Invoices,
    // TabComponent: UnderConstruction,
    hide: ({ appRoleDetails, projectData }) => {
      const projectUserRole = projectData?.projUserRole;
      if (isHomeOwner(appRoleDetails)) {
        return true;
      }
      if (isProjectLimitedView(appRoleDetails, projectUserRole)) {
        return true;
      }
      return false;
    },
  },
  {
    key: "changeOrders",
    label: "Change Orders",
    TabComponent: ChangeOrders,
  },
  {
    key: "team",
    label: "Project Team",
    TabComponent: UserManagement,
    hide: ({ appRoleDetails, projectData }) => {
      const projectUserRole = projectData?.projUserRole;
      return (
        !isProjectManager(appRoleDetails, projectUserRole) ||
        isAssignee(projectData.accessType) ||
        isHomeOwner(appRoleDetails)
      );
    },
  },
  // {
  //   key: "propertyDetails",
  //   label: "Property Details",
  //   TabComponent: ViewPropertyDetails,
  //   hide: ({ companyConfig }) => !companyConfig?.projEqualProp,
  // },
  {
    key: "punchList",
    label: "Punch List",
    TabComponent: PunchListPage,
  },
  {
    key: "warranty",
    label: "Warranty",
    TabComponent: Warranty,
  },
];

export const ViewProject = () => {
  const appRoleDetails = useContext(AppRoleContext);
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  // const  = location
  const [selectedTab, updateTab] = React.useState("overview");

  const params = useParams();

  const dispatch = useDispatch();
  const projectData = useSelector(getProjectData);
  const companyConfig = useSelector(getCompanyConfig);

  const getProjectDetails = useCallback(() => {
    dispatch(fetchProjectById(params.projectId));
  }, [dispatch, params.projectId]);

  useEffect(() => {
    if (params.tab) {
      updateTab(params.tab);
    }
  }, [params]);

  useEffect(() => {
    getProjectDetails();
  }, [dispatch, getProjectDetails]);

  useEffect(() => {
    if (get(projectData, "accessType", false) && selectedTab === "overview") {
      updateTab(isAssignee(projectData.accessType) ? "jobs" : "overview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData]);

  return (
    <>
      <div className="ms-3 mt-2 mb-1">
        <button
          className="outlinedButton"
          style={{
            width: "fit-content",
            height: "1.7rem",
            display: "flex",
            alignItems: "center",
          }}
          onClick={() => {
            window.history.back();
          }}
        >
          <ArrowBack
            style={{
              height: "20px",
              width: "23px",
              color: "#345DAE",
              marginBottom: "0.4rem",
            }}
            className="pointer mt-2"
          />
          Property
        </button>
      </div>
      <div className="d-flex align-items-center ms-2">
        <Typography
          className="h3 mt-2"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "15rem",
          }}
        >
          {projectData?.projectName}
        </Typography>
      </div>
      <CustomBreadCrumb
        breadCrumbItems={
          companyConfig?.projEqualProp
            ? ["properties", "projectName"]
            : ["properties", "propertyName", "projectName"]
        }
      />
      <div className="project-tabs tabs" id="tab-container">
        <RenderTabs
          tabs={tabs}
          selectedTab={selectedTab}
          onTabClick={(newTab) => {
            updateTab(newTab);
            navigate(
              `/properties/${params.propertyId}/projects/${params.projectId}/${newTab}`,
              {
                state,
              }
            );
          }}
          tabParams={{
            projectId: params.projectId,
            propertyId: params.propertyId,
            projectData,
            getProjectDetails,
            appRoleDetails,
            companyConfig,
          }}
        />
      </div>
    </>
  );
};

export default ViewProject;
